import { DOCUMENT } from '@angular/common';
import {
  EnvironmentProviders,
  Injectable,
  Provider,
  inject,
  signal,
  provideAppInitializer,
} from '@angular/core';

// NOTE (step 11.2): super-user no longer uses anything in this folder (it always
// runs the new look). Kept, with its specs, for the customer apps' go-wide step.
export type ThemeMode = 'legacy' | 'next';

const STORAGE_KEY = 'shipm8.theme-mode';
const NEXT_BODY_CLASS = 'sm-next';

/**
 * Reads the stored theme mode, defaulting to 'legacy'. Safe to call at
 * bootstrap (e.g. to pick the preset passed to providePrimeNG), before
 * Angular is running.
 *
 * The mode is fixed for the lifetime of the page: switching saves the new
 * mode and reloads the app, so PrimeNG is always configured once, at startup,
 * with the right preset. Switching presets in a running app goes through
 * PrimeNG internals that don't handle it reliably.
 */
export function readThemeMode(): ThemeMode {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'next' ? 'next' : 'legacy';
  } catch {
    return 'legacy';
  }
}

/** Sets the body class for the current theme mode when the app starts. */
export function provideThemeMode(): (Provider | EnvironmentProviders)[] {
  return [
    provideAppInitializer(() => {
      const initializerFn = (
        (service: ThemeModeService) => () =>
          service.init()
      )(inject(ThemeModeService));
      return initializerFn();
    }),
  ];
}

@Injectable({ providedIn: 'root' })
export class ThemeModeService {
  private readonly _document = inject(DOCUMENT);

  readonly mode = signal<ThemeMode>(readThemeMode()).asReadonly();

  init(): void {
    this._document.body.classList.toggle(
      NEXT_BODY_CLASS,
      this.mode() === 'next'
    );
  }

  /** Saves the mode and reloads the app so it starts with the new theme. */
  setMode(mode: ThemeMode): void {
    if (mode === this.mode()) return;
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Storage unavailable: nothing to persist, so a reload would change nothing.
      return;
    }
    this.reload();
  }

  toggle(): void {
    this.setMode(this.mode() === 'legacy' ? 'next' : 'legacy');
  }

  protected reload(): void {
    this._document.defaultView?.location.reload();
  }
}
